import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import SignaturePad from "react-signature-canvas";
import { collection, getDocs, addDoc, serverTimestamp, doc, updateDoc, Timestamp, getDoc, runTransaction } from "firebase/firestore";
import { ref as storageRef, uploadBytes, getDownloadURL, listAll, deleteObject } from "firebase/storage";
import { db, storage, auth } from "./firebase";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { getCurrentUserCompanyId, getCurrentUserProfile } from "./utils/userUtils";
import { getOrCreateDraft, saveDraft, submitDraft, getUserDrafts, deleteDraft, validateSubmission } from "./utils/draftUtils";
import { debounce } from "./utils/debounce";
import logo from "./assets/image/logo_web.png";
import Banner from './components/Banner';
import { calculateNextDueDate } from "./utils/scheduleUtils";
import { createApprovalRequests, APPROVAL_STATUS } from "./utils/approvalUtils";
import AssetField from "./components/fields/AssetField";
import FieldRenderer from "./components/FieldRenderer";
import LocationField from "./components/fields/LocationField";
import ScheduledChecksView from "./components/ScheduledChecksView";
import SectionRenderer from "./components/fields/SectionRenderer";
import SliderField from "./components/fields/SliderField";
import { buildWorkflowSnapshot, canStartWorkflowFromScratch, filterVisibleWorkflowFields, getNextWorkflowStep, hasSplitWorkflow, isWorkflowSection } from "./utils/workflowUtils";
import EvidenceNotesBlock from "./components/fields/EvidenceNotesBlock";
import AttachmentModal from "./components/AttachmentModal";


// Branching logic: compute which questions are visible based on answers
const computeVisible = (fields, answers) => {
  const visible = {};
  if (!Array.isArray(fields)) return visible;
  // All fields visible by default (keyed by id/label/question)
  fields.forEach(f => {
    const key = f.id || f.label || f.question;
    if (key) visible[key] = true;
  });
  // Apply branching for yesno/checkbox
  fields.forEach(f => {
    const key = f.id || f.label || f.question;
    if (!key) return;
    if (f.branchMap && (f.type === 'yesno' || f.type === 'checkbox')) {
      const val = answers[key];
      if (val === true && f.branchMap.yes && Array.isArray(f.branchMap.yes.targets)) {
        f.branchMap.yes.targets.forEach(tid => visible[tid] = true);
      }
      if (val === false && f.branchMap.no && Array.isArray(f.branchMap.no.targets)) {
        f.branchMap.no.targets.forEach(tid => visible[tid] = true);
      }
    }
  });
  // Hide branch targets that are not activated
  fields.forEach(f => {
    const key = f.id || f.label || f.question;
    if (!key) return;
    if (f.branchMap && (f.type === 'yesno' || f.type === 'checkbox')) {
      if (f.branchMap.yes && Array.isArray(f.branchMap.yes.targets)) {
        f.branchMap.yes.targets.forEach(tid => {
          if (answers[key] !== true) visible[tid] = false;
        });
      }
      if (f.branchMap.no && Array.isArray(f.branchMap.no.targets)) {
        f.branchMap.no.targets.forEach(tid => {
          if (answers[key] !== false) visible[tid] = false;
        });
      }
    }
  });
  return visible;
};

export default function ConductCheck() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  // User info state (no explicit name field; forms can include a text field if needed)
  // signatureData per-field key -> dataURL
  const [signatureData, setSignatureData] = useState({});
  const [showSigError, setShowSigError] = useState(false);
  // signature pad refs stored by field key
  const signatureRefs = useRef({});
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeForm, setActiveForm] = useState(null);
  const [answers, setAnswers] = useState({});
  // Universal per-field notes and evidence state
  const [notes, setNotes] = useState({});
  const [evidence, setEvidence] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [filePreviews, setFilePreviews] = useState({});
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const [companyId, setCompanyId] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") === "scheduled" ? "scheduled" : "conduct"); // "conduct" | "scheduled"
  
  // DRAFT-RELATED STATE
  const [currentDraftId, setCurrentDraftId] = useState(null);
  const [draftSectionProgress, setDraftSectionProgress] = useState({ currentSectionIndex: 0, completedSections: [] });
  const [isDraft, setIsDraft] = useState(false);
  const [userDrafts, setUserDrafts] = useState([]);
  const [autoSaving, setAutoSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  
  const navigate = useNavigate();

  useEffect(() => {
    getCurrentUserProfile()
      .then((profile) => setUserProfile(profile || null))
      .catch((err) => console.warn("Failed to load current user profile", err));
  }, []);

  // ---------- Field-completion progress bar ----------
  const NON_ANSWERABLE_TYPES = new Set(['section', 'split_workflow', 'instruction', 'info', 'header', 'label', 'divider']);
  const visibleWorkflowFields = useMemo(
    () => filterVisibleWorkflowFields(activeForm?.fields || [], userProfile, draftSectionProgress),
    [activeForm?.fields, userProfile, draftSectionProgress]
  );
  const workflowSections = useMemo(
    () => buildWorkflowSnapshot(activeForm?.fields || []),
    [activeForm?.fields]
  );
  const availableForms = useMemo(
    () => forms.filter((form) => {
      if (!hasSplitWorkflow(form?.fields || [])) {
        return true;
      }
      return canStartWorkflowFromScratch(form?.fields || [], userProfile);
    }),
    [forms, userProfile]
  );

  const fieldProgress = useMemo(() => {
    if (!activeForm || !visibleWorkflowFields.length) return { answered: 0, total: 0, pct: 0 };
    const visible = computeVisible(visibleWorkflowFields, answers);
    let total = 0;
    let answered = 0;
    visibleWorkflowFields.forEach((f) => {
      const key = f.id || f.label || f.question;
      if (!key || !visible[key]) return;
      if (NON_ANSWERABLE_TYPES.has(f.type)) return;
      total++;
      const val = answers[key];
      if (val !== undefined && val !== null && val !== '' && !(Array.isArray(val) && val.length === 0)) {
        answered++;
      }
    });
    return { answered, total, pct: total > 0 ? Math.round((answered / total) * 100) : 0 };
  }, [activeForm, answers, visibleWorkflowFields]);

  // Helper to detect uploaded draft files
  const isUploadedFile = (v) => v && v._fileType === 'uploaded' && v.url;

  // Ensure fields are always presented as an array.
  const normalizeFields = (raw) => {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    if (typeof raw === "string") {
      try {
        const parsed = JSON.parse(raw);
        return normalizeFields(parsed);
      } catch (e) {
        return [];
      }
    }
    if (typeof raw === "object") {
      // If it's already a keyed object of fieldId -> fieldData, convert to array
      return Object.entries(raw).map(([k, v]) => {
        if (v && typeof v === "object") return { id: v.id || k, ...v };
        // if value is primitive, use as question text
        return { id: k, question: String(v) };
      });
    }
    return [];
  };

  const flattenFields = (items) => {
    if (!Array.isArray(items)) return [];
    const out = [];
    for (const field of items) {
      // Push only the top-level field exactly as stored
      out.push(field);
      // Do NOT flatten children of sections or nested fields here.
      // SectionRenderer is responsible for rendering any nested content.
    }
    return out;
  };

  // Load forms when component mounts
  useEffect(() => {
    async function loadForms() {
      setLoading(true);
      try {
        const userCompanyId = await getCurrentUserCompanyId();
        if (!userCompanyId) {
          setError(t('common.noCompany'));
          setLoading(false);
          return;
        }
        setCompanyId(userCompanyId);
        const col = collection(db, `companies/${userCompanyId}/forms`);
        const snap = await getDocs(col);
        const list = snap.docs.map((d) => {
          const data = d.data() || {};
          const rawFields = normalizeFields(data.fields);
          return { id: d.id, ...data, fields: flattenFields(rawFields) };
        });
        console.debug("Loaded forms:", list);
        setForms(list);
      } catch (err) {
        console.error(err);
        setError(t('checks.failedLoadForms'));
      } finally {
        setLoading(false);
      }
    }
    loadForms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load user drafts when company is set
  useEffect(() => {
    if (companyId && auth.currentUser) {
      loadDrafts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, userProfile]);

  // Auto-save draft when answers change (files already uploaded to Storage)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSaveDraft = useCallback(
    debounce(async (draftId, data) => {
      if (!companyId || !draftId) return;
      try {
        setAutoSaving(true);
        
        // Data already contains URLs from uploaded files, just save as-is
        await saveDraft(companyId, draftId, data);
        setLastSaved(new Date());
      } catch (error) {
        console.error("Auto-save failed:", error);
      } finally {
        setAutoSaving(false);
      }
    }, 800),
    [companyId]
  );

  // Trigger auto-save when answers, notes, or evidence changes
  useEffect(() => {
    if (isDraft && currentDraftId && activeForm) {
      debouncedSaveDraft(currentDraftId, {
        answers,
        notes,
        evidence,
        signatureData,
        workflowSections,
        sectionProgress: draftSectionProgress,
      });
    }
  }, [answers, notes, evidence, signatureData, isDraft, currentDraftId, activeForm, debouncedSaveDraft, workflowSections, draftSectionProgress]);

  // Load user's draft submissions
  async function loadDrafts() {
    try {
      const drafts = await getUserDrafts(companyId, userProfile || auth.currentUser.uid);
      setUserDrafts(drafts);
    } catch (error) {
      console.error("Failed to load drafts:", error);
    }
  }

  // Start a new check or resume draft
  const startForm = async (form) => {
    // Check for existing draft
    if (companyId && auth.currentUser) {
      const existingDraft = userDrafts.find(d => d.formId === form.id);
      
      if (existingDraft) {
        const resume = window.confirm(
          `You have a draft for "${form.title || 'this form'}" saved ${new Date(existingDraft.updatedAt?.toDate?.() || existingDraft.updatedAt).toLocaleString()}.\n\nResume draft or start new?`
        );
        
        if (resume) {
          await resumeDraft(existingDraft);
          return;
        } else {
          // User wants to start fresh, delete old draft
          await deleteDraft(companyId, existingDraft.id);
          await loadDrafts();
        }
      }
      
      // Create new draft
      try {
        const draft = await getOrCreateDraft(companyId, form, auth.currentUser.uid, null, userProfile);
        setCurrentDraftId(draft.id);
        setDraftSectionProgress(draft.sectionProgress || { currentSectionIndex: 0, completedSections: [] });
        setIsDraft(true);
      } catch (error) {
        console.error("Failed to create draft:", error);
      }
    }

    const raw = normalizeFields(form.fields);
    const normalized = { ...form, fields: flattenFields(raw) };
    console.debug("Starting form, normalized:", normalized);
    setActiveForm(normalized);
    const initial = {};
    (normalized.fields || []).forEach((f, idx) => {
      const key = f.id || f.key || f.label || `q_${idx}`;
      if (f.type === 'checkbox') {
        initial[key] = false;
      } else if (f.type === 'location') {
        initial[key] = {};
      } else if (f.type === 'document_number') {
        try {
          const cfg = f.config || {};
          const pad = Math.max(1, cfg.pad || 1);
          const next = cfg.nextNumber || 1;
          initial[key] = `${cfg.prefix || ""}${String(next).padStart(pad, "0")}`;
        } catch (e) {
          initial[key] = "";
        }
      } else if (
        f.type === 'slider' ||
        ((f.type === 'number' || f.type === 'text') && (
          [f.config?.style, f.config?.control, f.config?.widget, f.config?.renderAs, f.config?.input, f.input, f.widget, f.ui, f.control, f.renderAs].filter(Boolean).includes('slider') ||
          f.config?.inputType === 'range' ||
          (typeof f.config?.min === 'number' && typeof f.config?.max === 'number' && (f.config?.useSlider !== false))
        ))
      ) {
        const min = (f.config && f.config.min !== undefined && f.config.min !== null) ? Number(f.config.min) : 0;
        initial[key] = min;
      } else {
        initial[key] = "";
      }
    });
    setAnswers(initial);
  };

  // Resume existing draft
  async function resumeDraft(draft) {
    const form = forms.find(f => f.id === draft.formId);
    if (!form) {
      alert(t('checks.formNotFound'));
      return;
    }

    const raw = normalizeFields(form.fields);
    const normalized = { ...form, fields: flattenFields(raw) };
    setActiveForm(normalized);
    setAnswers(draft.answers || {});
    setNotes(draft.notes || {});
    setEvidence(draft.evidence || {});
    setSignatureData(draft.signatureData || {});
    setCurrentDraftId(draft.id);
    setDraftSectionProgress(draft.sectionProgress || { currentSectionIndex: 0, completedSections: [] });
    setIsDraft(true);
    setLastSaved(draft.updatedAt?.toDate?.() || new Date(draft.updatedAt));

    // Hydrate previews from uploaded file URLs
    const previews = {};
    Object.entries(draft.answers || {}).forEach(([key, val]) => {
      if (val?._fileType === 'uploaded' && val.url) {
        previews[key] = val.url;
      }
    });
    Object.entries(draft.evidence || {}).forEach(([key, val]) => {
      if (val?._fileType === 'uploaded' && val.url) {
        previews[key + '_evidence'] = val.url;
      }
    });
    setFilePreviews(previews);
  }

  // Save and exit (return to form list)
  async function saveAndExit() {
    if (!companyId) {
      alert(t('checks.errorCompanyNotFound'));
      return;
    }
    
    if (!currentDraftId) {
      alert(t('checks.errorNoDraft'));
      return;
    }
    
    try {
      const workflowStep = getNextWorkflowStep(workflowSections, draftSectionProgress);
      const nextProgress = workflowStep.hasWorkflow ? workflowStep.nextProgress : draftSectionProgress;
      // Files already uploaded, just save current state
      await saveDraft(companyId, currentDraftId, {
        answers,
        notes,
        evidence,
        signatureData,
        workflowSections,
        sectionProgress: nextProgress,
      });
      
      alert(
        workflowStep.hasWorkflow && !workflowStep.isFinalSection
          ? t('checks.workflowContinueMessage', {
              current: workflowStep.currentSection?.label || t('checks.workflowSectionFallback'),
              next: workflowStep.nextSection?.label || t('checks.workflowNextSectionFallback'),
            })
          : t('checks.progressSaved')
      );
      setActiveForm(null);
      setIsDraft(false);
      setCurrentDraftId(null);
      setDraftSectionProgress({ currentSectionIndex: 0, completedSections: [] });
      await loadDrafts();
    } catch (error) {
      console.error("Failed to save:", error);
      alert(t('checks.failedSaveProgress') + error.message);
    }
  }

  const startScheduledCheck = (formId, scheduleId) => {
    const form = forms.find(f => f.id === formId);
    if (form) {
      const raw = normalizeFields(form.fields);
      const normalized = { ...form, fields: flattenFields(raw), scheduleId };
      setActiveForm(normalized);
      const initial = {};
      (normalized.fields || []).forEach((f, idx) => {
        const key = f.id || f.key || f.label || `q_${idx}`;
        if (f.type === 'checkbox') {
          initial[key] = false;
        } else if (f.type === 'location') {
          initial[key] = {};
        } else if (f.type === 'document_number') {
          try {
            const cfg = f.config || {};
            const pad = Math.max(1, cfg.pad || 1);
            const next = cfg.nextNumber || 1;
            initial[key] = `${cfg.prefix || ""}${String(next).padStart(pad, "0")}`;
          } catch (e) {
            initial[key] = "";
          }
        } else {
          initial[key] = "";
        }
      });
      setAnswers(initial);
      setActiveTab("conduct"); // Switch to conduct tab
    }
  };

  const setAnswer = (key, value) => {
    setAnswers((s) => ({ ...s, [key]: value }));
  };

  const handleFileChange = async (key, files) => {
    if (!files?.[0] || !currentDraftId) return;

    const file = files[0];

    try {
      // Upload file immediately to draft storage
      const url = await uploadDraftFile(file, currentDraftId, key);

      // Store URL and metadata in answers
      setAnswers(prev => ({
        ...prev,
        [key]: {
          _fileType: 'uploaded',
          url,
          name: file.name,
          type: file.type,
          size: file.size
        }
      }));

      // Set preview from uploaded URL
      setFilePreviews(prev => ({
        ...prev,
        [key]: url
      }));
    } catch (error) {
      console.error('Failed to upload draft file:', error);
      alert(t('checks.failedUploadFile') + error.message);
    }
  };

  const uploadFile = async (file, formId, fieldKey) => {
    const path = `companies/${companyId}/submissions/${formId}/${fieldKey}/${Date.now()}_${file.name}`;
    const r = storageRef(storage, path);
    await uploadBytes(r, file);
    const url = await getDownloadURL(r);
    return url;
  };

  const uploadDraftFile = async (file, draftId, fieldKey) => {
    const path = `companies/${companyId}/drafts/${draftId}/${fieldKey}/${Date.now()}_${file.name}`;
    const r = storageRef(storage, path);
    await uploadBytes(r, file);
    return await getDownloadURL(r);
  };

  const cleanupDraftFiles = async (draftId) => {
    try {
      const folder = storageRef(storage, `companies/${companyId}/drafts/${draftId}`);
      const files = await listAll(folder);
      await Promise.all(files.items.map(item => deleteObject(item)));
      console.log('Draft files cleaned up:', draftId);
    } catch (error) {
      console.warn('Failed to cleanup draft files:', error);
      // Don't fail submission if cleanup fails
    }
  };

  const submit = async () => {
    if (!activeForm) return;
    
    // If this is a draft, validate using the draft utility
    if (isDraft && currentDraftId) {
      const validation = validateSubmission(activeForm, answers);
      if (!validation.valid) {
        setError(
          `Please complete required fields:\n${validation.missingFields.map(f => f.fieldLabel).join('\n')}`
        );
        return;
      }
    }
    
    // Validation: ensure required signatures exist (name is optional and handled by form fields)
    // If there are signature fields required, ensure each visible signature has data
    const visibleForValidation = computeVisible(visibleWorkflowFields, answers);
    const missingSignatures = visibleWorkflowFields.filter((f, idx) => {
      const key = f.id || f.label || f.question || `q_${idx}`;
      return f.type === 'signature' && f.required && visibleForValidation[key] && !signatureData[key];
    }).map(f => f.label || f.question || f.id);
    if (missingSignatures.length) {
      setShowSigError(true);
      setError("Please sign the required fields: " + missingSignatures.join(', '));
      return;
    }
    // Validate required fields (only those currently visible)
    const missing = [];
    const visible = visibleForValidation; // computed above
    visibleWorkflowFields.forEach((f, idx) => {
      const key = f.id || f.label || f.question || `q_${idx}`;
      if (!visible[key]) return; // skip hidden fields
      if (f.type === 'signature') return; // handled earlier
      if (f.required && (!answers[key] || answers[key] === "" || answers[key] === false)) {
        missing.push(f.question || f.label || `Question ${idx + 1}`);
      }
      if ((f.type === "media" || f.type === "photo") && f.required && !answers[key]) {
        missing.push(f.question || f.label || `Question ${idx + 1}`);
      }
    });
    if (missing.length) {
      setError("Please complete required fields: " + missing.join(", "));
      return;
    }
    setSubmitting(true);
    try {
      // Build a field map so we can include labels
      const fieldMap = {};
      visibleWorkflowFields.forEach((f, idx) => {
        const k = f.id || f.label || f.question || `q_${idx}`;
        fieldMap[k] = f;
      });

      // Helper to convert section child field IDs to labels
      const convertSectionValue = async (sectionField, sectionValue) => {
        const childFields = Array.isArray(sectionField.children) && sectionField.children.length > 0
          ? sectionField.children
          : Array.isArray(sectionField.fields)
            ? sectionField.fields
            : [];
        if (!sectionValue || !childFields.length) return sectionValue;
        
        // Build child field map
        const childMap = {};
        childFields.forEach(child => {
          const childId = child.id || child.label || child.question;
          if (childId) childMap[childId] = child;
        });
        
        // Helper to extract actual value from field response objects
        const extractValue = async (childVal, childField, childId) => {
          if (!childVal) return "";
          
          // Handle uploaded files
          if (childVal._fileType === 'uploaded') {
            return childVal.url;
          }
          
          // Handle objects with specific value properties based on field type
          if (typeof childVal === 'object') {
            const fieldType = childField?.type;
            
            // File upload field: extract and upload files if needed
            if (childVal.files && Array.isArray(childVal.files)) {
              if (childVal.files.length > 0) {
                const firstFile = childVal.files[0];
                
                // If it's a File object, upload it first
                if (firstFile instanceof File) {
                  const url = await uploadFile(firstFile, activeForm.id || "unknown-form", childId);
                  return url;
                }
                
                // Otherwise return the URL
                return firstFile.url || firstFile.name || String(firstFile);
              }
              return "";
            }
            
            // Text field
            if ('text' in childVal) return childVal.text || "";
            
            // Number/slider field
            if ('number' in childVal) return childVal.number;
            
            // Select/dropdown
            if ('selected' in childVal) return childVal.selected || "";
            
            // Radio
            if ('value' in childVal) return childVal.value;
            
            // Signature
            if ('signature' in childVal) return childVal.signature || "";
            
            // If it's just a wrapper object, try to extract any value
            const keys = Object.keys(childVal);
            if (keys.length === 1) {
              return childVal[keys[0]];
            }
            
            // Fallback: return as-is if can't extract
            return childVal;
          }
          
          // Primitive values
          return childVal;
        };
        
        // Convert array of objects (repeatable sections)
        if (Array.isArray(sectionValue)) {
          const convertedInstances = [];
          for (const item of sectionValue) {
            if (typeof item !== 'object' || item === null) {
              convertedInstances.push(item);
              continue;
            }
            const converted = {};
            for (const [childId, childVal] of Object.entries(item)) {
              const childField = childMap[childId];
              const childLabel = childField ? (childField.label || childField.question || childId) : childId;
              converted[childLabel] = await extractValue(childVal, childField, childId);
            }
            convertedInstances.push(converted);
          }
          return convertedInstances;
        }
        
        // Convert single object (non-repeatable sections)
        if (typeof sectionValue === 'object') {
          const converted = {};
          for (const [childId, childVal] of Object.entries(sectionValue)) {
            const childField = childMap[childId];
            const childLabel = childField ? (childField.label || childField.question || childId) : childId;
            converted[childLabel] = await extractValue(childVal, childField, childId);
          }
          return converted;
        }
        
        return sectionValue;
      };

      // Process any file uploads in answers and build labeled answers
      const finalAnswers = {};
      for (const [k, v] of Object.entries(answers)) {
        const field = fieldMap[k] || {};
        const label = field.label || field.question || k;
        
        // Handle section fields - convert child IDs to labels
        if (isWorkflowSection(field)) {
          const convertedValue = await convertSectionValue(field, v);
          finalAnswers[k] = { label, value: convertedValue };
          continue;
        }
        
        // Handle asset fields - store full asset snapshot
        if (field.type === 'asset') {
          // AssetField returns an array of asset objects
          if (Array.isArray(v) && v.length > 0) {
            // For single-select, store the first asset object directly
            // For multi-select, store the array
            const assetValue = field.config?.multiSelect ? v : v[0];
            finalAnswers[k] = { label, value: assetValue };
          } else {
            finalAnswers[k] = { label, value: null };
          }
          continue;
        }
        
        // Handle uploaded draft files
        if (v?._fileType === 'uploaded') {
          finalAnswers[k] = { label, value: v.url };
          continue;
        }
        
        // Handle file upload field with {files: [...]} wrapper
        if (v && typeof v === 'object' && v.files && Array.isArray(v.files)) {
          if (v.files.length > 0) {
            const firstFile = v.files[0];
            // If it's a File object, upload it
            if (firstFile instanceof File) {
              const url = await uploadFile(firstFile, activeForm.id || "unknown-form", k);
              finalAnswers[k] = { label, value: url };
              continue;
            }
            // Otherwise use the URL
            finalAnswers[k] = { label, value: firstFile.url || firstFile.name || String(firstFile) };
            continue;
          }
          // Empty files array
          finalAnswers[k] = { label, value: "" };
          continue;
        }
        
        // Handle regular file uploads (shouldn't happen with new flow, but keeping for safety)
        if (v && v instanceof File) {
          const url = await uploadFile(v, activeForm.id || "unknown-form", k);
          finalAnswers[k] = { label, value: url };
          continue;
        }
        if (Array.isArray(v) && v.length && v[0] instanceof File) {
          const urls = [];
          for (const file of v) {
            urls.push(await uploadFile(file, activeForm.id || "unknown-form", k));
          }
          finalAnswers[k] = { label, value: urls };
          continue;
        }
        // signatures
        if (field.type === 'signature') {
          finalAnswers[k] = { label, value: signatureData[k] || null };
          continue;
        }
        finalAnswers[k] = { label, value: v };
      }

      // Append per-field notes and evidence uploads
      for (const f of visibleWorkflowFields) {
        const key = f.id || f.label || f.question;
        if (!key) continue;
        
        const fieldLabel = f.label || f.question || key;
        
        // Only add notes if they exist
        if (notes[key]) {
          finalAnswers[key + "_notes"] = {
            label: fieldLabel + " - Notes",
            value: notes[key]
          };
        }
        
        // Only add evidence if it exists
        if (evidence[key]) {
          // Handle uploaded draft evidence
          if (evidence[key]?._fileType === 'uploaded') {
            finalAnswers[key + "_evidence"] = {
              label: fieldLabel + " - Evidence",
              value: evidence[key].url
            };
          } else if (evidence[key] instanceof File) {
            const url = await uploadFile(evidence[key], activeForm.id || "unknown-form", key + "_evidence");
            finalAnswers[key + "_evidence"] = {
              label: fieldLabel + " - Evidence",
              value: url
            };
          }
        }
      }

      // If the form contains a Document Number field, compute and reserve its current value
      // using a Firestore transaction to avoid duplicates under concurrency.
      let computedDocumentNumber = null;
      try {
        if (activeForm?.id) {
          const formRef = doc(db, `companies/${companyId}/forms/${activeForm.id}`);
          computedDocumentNumber = await runTransaction(db, async (tx) => {
            const snap = await tx.get(formRef);
            if (!snap.exists()) return null;
            const data = snap.data() || {};
            const storedFields = data.fields;
            if (!Array.isArray(storedFields)) {
              // If stored fields are not an array, fallback to reading from activeForm
              const docField = (activeForm.fields || []).find((f) => f.type === "document_number");
              if (!docField) return null;
              const cfg = docField.config || {};
              const pad = Math.max(1, cfg.pad || 1);
              const next = cfg.nextNumber || 1;
              return `${cfg.prefix || ""}${String(next).padStart(pad, "0")}`;
            }

            // Find the first document_number field in the stored fields
            const idx = storedFields.findIndex((f) => f?.type === "document_number");
            if (idx === -1) return null;

            const field = storedFields[idx];
            const cfg = field.config || {};
            const pad = Math.max(1, cfg.pad || 1);
            const next = cfg.nextNumber || 1;
            const assigned = `${cfg.prefix || ""}${String(next).padStart(pad, "0")}`;

            // Update nextNumber atomically
            const updatedFields = [...storedFields];
            updatedFields[idx] = { ...field, config: { ...cfg, nextNumber: next + 1 } };

            tx.update(formRef, { fields: updatedFields });
            return assigned;
          });
        }
      } catch (e) {
        console.warn("Failed to reserve document number (transaction)", e);
        // fall back to non-transactional compute if possible
        try {
          const docField = (activeForm.fields || []).find((f) => f.type === "document_number");
          if (docField) {
            const cfg = docField.config || {};
            const pad = Math.max(1, cfg.pad || 1);
            const next = cfg.nextNumber || 1;
            computedDocumentNumber = `${cfg.prefix || ""}${String(next).padStart(pad, "0")}`;
          }
        } catch (ee) {
          console.warn('Failed fallback compute for document number', ee);
        }
      }

      const payload = {
        companyId: companyId,
        formId: activeForm.id || null,
        formTitle: activeForm.title || activeForm.formTitle || "",
        formDef: activeForm.fields || [],
        createdAt: serverTimestamp(),
        submittedAt: new Date().toISOString(),
        completedAt: Timestamp.now(),
        createdBy: auth.currentUser.uid, // Required by Firestore rules
        conductedBy: auth.currentUser.uid,
        conductedByName: auth.currentUser?.displayName || null,
        devicePlatform: "web",
        createdVia: "web",
        lastEditedVia: "web",
        allowMobileEdit: false,
        source: "web",
        status: "submitted",
        // userName intentionally omitted; forms that require a name should include a text field
        responses: finalAnswers, // Changed from 'answers' to 'responses' for clarity
        scheduleId: activeForm.scheduleId || null,
        documentNumber: computedDocumentNumber,
        approvalStatus: APPROVAL_STATUS.NOT_REQUIRED, // Default, will be updated if approval workflow exists
        workflowSections,
      };

      const workflowStep = getNextWorkflowStep(workflowSections, draftSectionProgress);

      if (isDraft && workflowStep.hasWorkflow && !workflowStep.isFinalSection) {
        await saveDraft(companyId, currentDraftId, {
          answers,
          notes,
          evidence,
          signatureData,
          workflowSections,
          sectionProgress: workflowStep.nextProgress,
        });
        setActiveForm(null);
        setIsDraft(false);
        setCurrentDraftId(null);
        setDraftSectionProgress({ currentSectionIndex: 0, completedSections: [] });
        await loadDrafts();
        alert(
          t('checks.workflowReadyMessage', {
            current: workflowStep.currentSection?.label || t('checks.workflowSectionFallback'),
            next: workflowStep.nextSection?.label || t('checks.workflowNextSectionFallback'),
          })
        );
        return;
      }

      let submissionRef;
      
      // If this is a draft being submitted, update the existing draft document
      if (isDraft && currentDraftId) {
        submissionRef = doc(db, `companies/${companyId}/submissions/${currentDraftId}`);
        await submitDraft(companyId, currentDraftId, payload);
        // Cleanup draft files after successful submission
        await cleanupDraftFiles(currentDraftId);
      } else {
        // Create new submission
        const col = collection(db, `companies/${companyId}/submissions`);
        submissionRef = await addDoc(col, payload);
      }

      // Check if form has approval workflow configured
      try {
        const workflowRef = doc(db, `companies/${companyId}/forms/${activeForm.id}/settings/approvalWorkflow`);
        const workflowSnap = await getDoc(workflowRef);
        
        console.log('Workflow document exists:', workflowSnap.exists());
        if (workflowSnap.exists()) {
          const workflowData = workflowSnap.data();
          console.log('Workflow data:', workflowData);
          console.log('Workflow enabled:', workflowData?.enabled);
        } else {
          console.log('No workflow document found for form:', activeForm.id);
        }
        
        if (workflowSnap.exists() && workflowSnap.data().enabled) {
          const workflow = workflowSnap.data();
          
          // Create approval requests from workflow configuration
          const approvalRequests = createApprovalRequests(workflow);
          console.log('Created approval requests:', approvalRequests);
          
          if (approvalRequests.length > 0) {
            // Save approvals to subcollection
            const approvalsCol = collection(db, `companies/${companyId}/submissions/${submissionRef.id}/approvals`);
            
            for (const approval of approvalRequests) {
              const approvalDoc = await addDoc(approvalsCol, {
                ...approval,
                createdAt: Timestamp.now(),
                updatedAt: Timestamp.now()
              });
              console.log('Created approval document:', approvalDoc.id, approval);
            }
            
            // Update submission approval status
            await updateDoc(submissionRef, {
              approvalStatus: APPROVAL_STATUS.PENDING,
              updatedAt: Timestamp.now()
            });
            
            // TODO: Send notifications to approvers
            console.log('Approval workflow triggered for submission:', submissionRef.id);
          }
        }
      } catch (approvalErr) {
        console.error("Error creating approval workflow:", approvalErr);
        // Don't fail submission if approval creation fails
      }

      // If this was a scheduled check, update the schedule
      if (activeForm.scheduleId) {
        try {
          const scheduleRef = doc(db, `companies/${companyId}/forms/${activeForm.id}/schedules/${activeForm.scheduleId}`);
          
          // Get current schedule to read frequency and customDays
          const scheduleSnap = await getDocs(collection(db, `companies/${companyId}/forms/${activeForm.id}/schedules`));
          const scheduleData = scheduleSnap.docs.find(d => d.id === activeForm.scheduleId)?.data();
          
          if (scheduleData) {
            const nextDue = calculateNextDueDate(
              scheduleData.frequency,
              new Date(),
              scheduleData.customDays
            );

            // Update schedule with completion info
            await updateDoc(scheduleRef, {
              lastCompleted: Timestamp.now(),
              nextDueDate: Timestamp.fromDate(nextDue),
              updatedAt: Timestamp.now()
            });

            // Create history entry
            const historyRef = collection(db, `companies/${companyId}/forms/${activeForm.id}/schedules/${activeForm.scheduleId}/history`);
            await addDoc(historyRef, {
              completedAt: Timestamp.now(),
              completedBy: auth.currentUser.uid,
              completedByName: auth.currentUser?.displayName || null,
              submissionId: submissionRef.id,
              dueDate: scheduleData.nextDueDate,
              createdAt: Timestamp.now()
            });
          }
        } catch (schedErr) {
          console.error("Error updating schedule:", schedErr);
          // Don't fail submission if schedule update fails
        }
      }

      // success — go to reports
      setDraftSectionProgress({ currentSectionIndex: 0, completedSections: [] });
      navigate("/reports");
    } catch (err) {
      console.error(err);
      setError(t('checks.failedSubmit'));
    } finally {
      setSubmitting(false);
      // revoke previews
      Object.values(filePreviews).forEach(u => { try { URL.revokeObjectURL(u); } catch(e) {} });
      setFilePreviews({});
    }
  };

  if (loading) return <div className="p-8">{t('checks.loadingForms')}</div>;
  if (error) return <div className="p-8 text-red-600">{error}</div>;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-black text-gray-100 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -left-24 -top-24 w-72 h-72 bg-rose-600/25 blur-3xl rounded-full" />
        <div className="absolute right-8 top-12 w-96 h-96 bg-amber-300/15 blur-3xl rounded-full" />
        <div className="absolute -right-32 bottom-12 w-96 h-96 bg-rose-500/15 blur-3xl rounded-full" />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <img src={logo} alt={t('common.appName')} className="w-12 h-12 object-contain" />
            <div>
              <p className="text-xs uppercase tracking-[0.24em] text-gray-400">{t('checks.conduct')}</p>
              <h1 className="text-3xl font-bold text-white">{t('checks.conductCheck')}</h1>
              <p className="text-sm text-gray-300">{t('checks.guidedInspections')}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              className="px-4 py-2 rounded-lg bg-rose-600 hover:bg-rose-700 text-white shadow-lg shadow-rose-900/30 transition"
              onClick={() => navigate('/dashboard')}
            >
              New Check
            </button>
            <button
              className="px-4 py-2 rounded-lg bg-white/10 border border-white/15 text-gray-100 hover:bg-white/15 transition"
              onClick={() => navigate(-1)}
            >
              {t('common.back')}
            </button>
          </div>
        </div>

        <div className="bg-white/5 border border-white/10 backdrop-blur-lg rounded-3xl p-4 lg:p-6 shadow-2xl">
          {/* Tab Navigation */}
          <div className="flex gap-2 border-b border-white/10 mb-4">
            <button
              onClick={() => setActiveTab("conduct")}
              className={`px-4 py-2 font-semibold transition-all border-b-2 ${
                activeTab === "conduct"
                  ? "border-rose-500 text-rose-200"
                  : "border-transparent text-gray-300 hover:text-white"
              }`}
            >
              {t('checks.conductCheckTab')}
            </button>
            <button
              onClick={() => setActiveTab("scheduled")}
              className={`px-4 py-2 font-semibold transition-all border-b-2 ${
                activeTab === "scheduled"
                  ? "border-rose-500 text-rose-200"
                  : "border-transparent text-gray-300 hover:text-white"
              }`}
            >
              {t('checks.scheduledTab')}
            </button>
          </div>

          {activeTab === "scheduled" ? (
            <div className="text-gray-100">
              <ScheduledChecksView companyId={companyId} onStartCheck={startScheduledCheck} />
            </div>
          ) : (
            <>
          {!activeForm ? (
            <div>
              {/* Drafts Section */}
              {userDrafts.length > 0 && (
                <div className="mb-6">
                  <h2 className="text-lg font-semibold mb-3 text-gray-800">{t('checks.resumeDraft')}</h2>
                  <ul className="space-y-3">
                    {userDrafts.map((draft) => (
                      <li key={draft.id} className="p-4 bg-yellow-50 rounded-lg shadow-sm border border-yellow-200">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-semibold text-gray-800">
                              {draft.formTitle || `Form ${draft.formId}`}
                              <span className="ml-2 px-2 py-1 text-xs bg-yellow-200 text-yellow-800 rounded">{t('common.draft')}</span>
                            </div>
                            <div className="text-sm text-gray-600">
                              {t('common.lastUpdated')} {new Date(draft.updatedAt?.toDate?.() || draft.updatedAt).toLocaleString()}
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <button
                              className="px-4 py-2 bg-blue-600 text-white rounded-md shadow-sm hover:bg-blue-700 transition"
                              onClick={() => resumeDraft(draft)}
                            >
                              {t('common.resume')}
                            </button>
                            <button
                              className="px-3 py-1 text-sm text-red-600 hover:text-red-800"
                              onClick={async () => {
                                if (window.confirm(t('checks.deleteThisDraft'))) {
                                  await deleteDraft(companyId, draft.id);
                                  await loadDrafts();
                                }
                              }}
                            >
                              {t('common.delete')}
                            </button>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Forms Section */}
              <p className="mb-4 text-gray-200">{t('checks.selectForm')}</p>
              <ul className="space-y-3">
                {availableForms.map((f) => (
                  <li key={f.id} className="p-4 bg-white rounded-lg shadow-sm border border-gray-200 text-gray-900">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-semibold text-gray-900">{f.title || f.formTitle || `Form ${f.id}`}</div>
                        <div className="text-sm text-gray-600">{(f.fields || []).length} {t('checks.questions')}</div>
                      </div>
                      <div className="flex items-center gap-3">
                        <button className="px-4 py-2 bg-rose-600 text-white rounded-md shadow-sm hover:bg-rose-700 transition" onClick={() => startForm(f)}>
                          Start
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
              {availableForms.length === 0 && (
                <p className="mt-4 text-sm text-gray-400">
                  {t('checks.noChecksReady')}
                </p>
              )}
            </div>
          ) : (
            <div>
              <h2 className="text-xl font-semibold mb-4">{activeForm.title || activeForm.formTitle}</h2>

              {/* Field completion progress bar */}
              {fieldProgress.total > 0 && (
                <div className="max-w-xl mx-auto mb-6">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-sm font-medium text-gray-700">
                      {fieldProgress.answered} of {fieldProgress.total} fields completed
                    </span>
                    <span className={`text-sm font-bold ${fieldProgress.pct === 100 ? 'text-emerald-600' : 'text-rose-600'}`}>
                      {fieldProgress.pct}%
                    </span>
                  </div>
                  <div className="w-full h-2.5 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ease-out ${
                        fieldProgress.pct === 100 ? 'bg-emerald-500' : 'bg-rose-600'
                      }`}
                      style={{ width: `${fieldProgress.pct}%` }}
                    />
                  </div>
                </div>
              )}

              <div className="space-y-6 max-w-xl mx-auto">
                {/* Name removed — include a text field in the form itself if a name is required */}

                {(() => {
                  const visible = computeVisible(visibleWorkflowFields, answers);
                  return visibleWorkflowFields.map((f, idx) => {
                    const key = f.id || f.label || f.question || `q_${idx}`;
                    if (!visible[key]) return null;
                    const sliderHints = new Set([
                      f?.config?.style,
                      f?.config?.control,
                      f?.config?.widget,
                      f?.config?.renderAs,
                      f?.config?.input,
                      f?.input,
                      f?.widget,
                      f?.ui,
                      f?.control,
                      f?.renderAs,
                    ].filter(Boolean));
                    const minRaw = f?.config?.min;
                    const maxRaw = f?.config?.max;
                    const minNum = minRaw !== undefined && minRaw !== null ? Number(minRaw) : undefined;
                    const maxNum = maxRaw !== undefined && maxRaw !== null ? Number(maxRaw) : undefined;
                    const hasMinMax = typeof minNum === "number" && !isNaN(minNum) && typeof maxNum === "number" && !isNaN(maxNum) && minNum < maxNum;
                    const isSliderLike = (
                      f.type === "slider" ||
                      (["number", "text"].includes(f.type) && (
                        sliderHints.has("slider") ||
                        f?.config?.inputType === "range" ||
                        (hasMinMax && f?.config?.useSlider !== false)
                      ))
                    );

                    return (
                      <div key={key} className="bg-white text-gray-900 rounded-lg shadow-sm p-6 mb-4 border border-gray-200">
                        <label className="font-medium block mb-2">{f.question || f.label}{f.required && <span className="text-red-600"> *</span>}</label>
                        {/* ---------------- BUILDER ATTACHMENTS ---------------- */}
                        {Array.isArray((f.attachments || f.config?.attachments)) && (f.attachments || f.config?.attachments).length > 0 && (
                          <div className="mb-3 space-y-2">
                            <div className="text-sm font-medium text-gray-700 flex items-center gap-2">{t('checks.attachments')}</div>
                            <div className="flex flex-wrap gap-3">
                              {(f.attachments || f.config?.attachments).map((att, i) => {
                                const isImg = att.type?.startsWith("image");
                                return (
                                  <div
                                    key={i}
                                    className="cursor-pointer border border-gray-300 rounded-lg p-2 hover:shadow-md transition"
                                    onClick={() => setPreviewAttachment(att)}
                                  >
                                    {isImg ? (
                                      <img
                                        src={att.url}
                                        alt={att.name || "attachment"}
                                        className="w-24 h-24 object-cover rounded"
                                      />
                                    ) : (
                                      <div className="flex items-center gap-2 text-blue-600 underline px-2 py-3">
                                        📄 {att.name || "File"}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        {f.type === "yesno" ? (
                          <div className="mt-2 space-x-2">
                            <button
                              className={`px-3 py-1 rounded ${answers[key] === true ? 'bg-rose-600 text-white' : 'bg-gray-200'}`}
                              onClick={() => setAnswer(key, true)}
                            >
                              {t('common.yes')}
                            </button>
                            <button
                              className={`px-3 py-1 rounded ${answers[key] === false ? 'bg-rose-600 text-white' : 'bg-gray-200'}`}
                              onClick={() => setAnswer(key, false)}
                            >
                              {t('common.no')}
                            </button>
                          </div>
                        ) : isSliderLike ? (
                          <SliderField
                            field={f}
                            value={typeof answers[key] === 'number' ? answers[key] : (typeof minNum === 'number' ? minNum : 0)}
                            setValue={(v) => setAnswer(key, v)}
                            previewMode={false}
                          />
                        ) : isWorkflowSection(f) ? (
                          <SectionRenderer
                            field={f}
                            value={answers[key] || (f.config?.repeatable ? [{}] : {})}
                            setValue={(v) => setAnswer(key, v)}
                            allFields={activeForm.fields}
                            previewMode={false}
                            renderChild={(childField, childValue, setChildValue) => (
                              <div className="mb-4">
                                <label className="font-medium block mb-1">{childField.label || childField.question}</label>
                                <FieldRenderer
                                  field={childField}
                                  value={childValue}
                                  setValue={setChildValue}
                                  formId={activeForm.id}
                                  allFields={activeForm.fields}
                                  previewMode={false}
                                  companyId={companyId}
                                  values={answers}
                                />
                              </div>
                            )}
                          />
                        ) : f.type === "text" ? (
                          <input
                            type="text"
                            value={answers[key] || ""}
                            onChange={e => setAnswer(key, e.target.value)}
                            className="w-full p-3 border border-gray-200 rounded-lg bg-white dark:bg-gray-800 text-gray-900 shadow-sm focus:ring-2 focus:ring-rose-600 focus:outline-none"
                          />
                        ) : f.type === "multiple_choice" ? (
                          <FieldRenderer
                            formId={activeForm.id}
                            field={f}
                            value={answers[key]}
                            setValue={(v) => setAnswer(key, v)}
                            allFields={activeForm.fields || []}
                            openViewer={() => {}}
                            previewMode={false}
                            companyId={companyId}
                            values={answers}
                          />
                        ) : f.type === "document_number" ? (
                          <div className="p-3 border border-gray-200 rounded-lg bg-white dark:bg-gray-800 shadow-sm">
                            <div className="font-mono text-lg text-gray-800 dark:text-gray-100">{answers[key] || (f.config?.prefix || "") + String(f.config?.nextNumber || 1).padStart(Math.max(1, f.config?.pad || 1), "0")}</div>
                            <div className="text-xs text-gray-500 mt-1">{t('forms.documentNumber')}</div>
                          </div>
                        ) : f.type === "number" ? (
                          <input
                            type="number"
                            value={answers[key] || ""}
                            onChange={e => setAnswer(key, e.target.value)}
                            className="w-full p-3 border border-gray-200 rounded-lg bg-white dark:bg-gray-800 text-gray-900 shadow-sm focus:ring-2 focus:ring-rose-600 focus:outline-none"
                          />
                        ) : f.type === "date_time" ? (
                          <input
                            // Respect field configuration: allow date-only, time-only, or combined date & time.
                            type={(f.config?.mode === "time") ? "time" : (f.config?.mode === "date" ? "date" : "datetime-local")}
                            value={answers[key] || ""}
                            onChange={e => setAnswer(key, e.target.value)}
                            className="w-full p-3 border border-gray-200 rounded-lg bg-white dark:bg-gray-800 text-gray-900 shadow-sm focus:ring-2 focus:ring-rose-600 focus:outline-none"
                          />
                        ) : f.type === "signature" ? (
                          <div className="mt-2">
                            <SignaturePad
                              ref={(el) => { if (el) signatureRefs.current[key] = el; }}
                              canvasProps={{ width: 400, height: 120, className: "border rounded" }}
                              onEnd={() => {
                                try {
                                  const pad = signatureRefs.current[key];
                                  if (!pad) return;
                                  const canvas = typeof pad.getCanvas === 'function' ? pad.getCanvas() : null;
                                  if (canvas && typeof canvas.toDataURL === 'function') {
                                    setSignatureData(prev => ({ ...prev, [key]: canvas.toDataURL("image/png") }));
                                    setShowSigError(false);
                                  }
                                } catch (e) {
                                  console.error('Signature capture failed', e);
                                }
                              }}
                              clearOnResize={false}
                            />
                            <div className="mt-2">
                              <button className="px-3 py-1 bg-gray-200 rounded" onClick={() => { const pad = signatureRefs.current[key]; if (pad && typeof pad.clear === 'function') pad.clear(); setSignatureData(prev => { const copy = { ...prev }; delete copy[key]; return copy; }); }}>{t('checks.clear')}</button>
                              {showSigError && !signatureData[key] && <div className="text-red-600 mt-2">{t('checks.signatureRequired')}</div>}
                            </div>
                          </div>
                        ) : f.type === "media" || f.type === "photo" ? (
                          <div>
                            {isUploadedFile(answers[key]) ? (
                              <div className="space-y-2">
                                {answers[key].type?.startsWith('image') ? (
                                  <img
                                    src={answers[key].url}
                                    alt={answers[key].name}
                                    className="rounded border max-w-full"
                                  />
                                ) : (
                                  <div className="p-3 border rounded bg-gray-50">
                                    <div className="text-sm text-gray-700">📄 {answers[key].name}</div>
                                    <a
                                      href={answers[key].url}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="text-xs text-blue-600 underline"
                                    >
                                      {t('checks.viewFile')}
                                    </a>
                                  </div>
                                )}
                                <button
                                  type="button"
                                  className="text-sm text-blue-600 underline hover:text-blue-800"
                                  onClick={() => {
                                    setAnswers(prev => {
                                      const c = { ...prev };
                                      delete c[key];
                                      return c;
                                    });
                                    setFilePreviews(prev => {
                                      const c = { ...prev };
                                      delete c[key];
                                      return c;
                                    });
                                  }}
                                >
                                  {t('checks.replaceFile')}
                                </button>
                              </div>
                            ) : (
                              <input
                                type="file"
                                onChange={e => handleFileChange(key, e.target.files)}
                                className="w-full p-2 border border-gray-200 rounded-lg bg-white dark:bg-gray-800 text-gray-900 shadow-sm"
                              />
                            )}
                          </div>
                        ) : f.type === "instruction" ? (
                          <div className="bg-gray-100 p-2 rounded text-gray-700">{f.config?.text || f.question || f.label}</div>
                        ) : f.type === "asset" ? (
                          <AssetField
                            field={f}
                            value={answers[key] || []}
                            onChange={(val) => setAnswer(key, val)}
                            companyId={companyId}
                          />
                        ) : f.type === "location" ? (
                          <LocationField
                            field={f}
                            value={answers[key]}
                            setValue={(v) => setAnswer(key, v)}
                            openViewer={() => {}}
                            renderInlineChildren={() => null}
                            previewMode={false}
                          />
                        ) : f.type === "checkbox" ? (
                          <label className="inline-flex items-center gap-3">
                            <input
                              type="checkbox"
                              checked={!!answers[key]}
                              onChange={e => setAnswer(key, e.target.checked)}
                              className="form-checkbox h-5 w-5 text-rose-600"
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-200">{f.config?.label || f.label || f.question || 'Select'}</span>
                          </label>
                        ) : (
                          <input
                            type="text"
                            value={answers[key] || ""}
                            onChange={e => setAnswer(key, e.target.value)}
                            className="w-full p-3 border border-gray-200 rounded-lg bg-white dark:bg-gray-800 text-gray-900 shadow-sm focus:ring-2 focus:ring-rose-600 focus:outline-none"
                          />
                        )}
                        {f.type !== "signature" && !isSliderLike && f.type !== "multiple_choice" && (
                          <EvidenceNotesBlock
                            fieldKey={key}
                            evidence={evidence}
                            setEvidence={setEvidence}
                            notes={notes}
                            setNotes={setNotes}
                            uploadDraftFile={uploadDraftFile}
                            currentDraftId={currentDraftId}
                          />
                        )}
                      </div>
                    );
                  });
                })()}

                <div className="mt-6 flex items-center gap-4">
                  <button
                    className="px-6 py-3 bg-rose-600 text-white rounded-md shadow-md hover:bg-rose-700 transition"
                    onClick={submit}
                    disabled={submitting}
                  >
                    {submitting ? t('common.submitting') : t('checks.submitCheck')}
                  </button>

                  {isDraft && (
                    <button
                      className="px-6 py-3 bg-blue-600 text-white rounded-md shadow-md hover:bg-blue-700 transition"
                      onClick={saveAndExit}
                      disabled={submitting || autoSaving}
                    >
                      {t('common.saveAndExit')}
                    </button>
                  )}

                  <button className="text-gray-700 hover:underline" onClick={() => setActiveForm(null)}>
                    {t('common.cancel')}
                  </button>

                  {isDraft && (
                    <div className="ml-auto text-sm text-gray-600">
                      {autoSaving ? (
                        <span className="flex items-center gap-2">
                          <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          {t('common.saving')}
                        </span>
                      ) : lastSaved ? (
                        <span>{t('common.lastSaved', { time: lastSaved.toLocaleTimeString() })}</span>
                      ) : null}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}
      </div>
    </div>
    {previewAttachment && (
      <AttachmentModal file={previewAttachment} onClose={() => setPreviewAttachment(null)} />
    )}
  </div>
  );
}
